/**
 * Credit Confirmation Modal
 *
 * ⚠️ FRONTEND REQUIREMENTS (from /docs/credits.md):
 *
 * 6.1 Display Rules
 * Every credit-consuming action must show: "This will use X credits"
 * This must appear before execution. No hidden costs.
 *
 * 6.2 Confirmation Modal
 * Every action must show:
 * "This action will generate a new output and use X credits.
 * Credits are deducted only after successful completion."
 *
 * 6.3 Failure Messaging
 * On failure: "The output could not be generated. No credits were deducted."
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Coins, Loader2 } from "lucide-react";
import {
  JobType,
  getCreditCost,
  getJobTypeDescription,
  formatCredits,
} from "@/types/credits";
import { checkSufficientCredits } from "@/services/creditService";

interface CreditConfirmationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onOpenChange: (open: boolean) => void;
  /** The type of job being executed */
  jobType: JobType | null;
  /** The user's ID */
  userId: string;
  /** Callback when user confirms the action */
  onConfirm: () => Promise<void>;
  /** Optional custom title */
  title?: string;
  /** Optional custom description */
  description?: string;
}

export function CreditConfirmationModal({
  open,
  onOpenChange,
  jobType,
  userId,
  onConfirm,
  title,
  description,
}: CreditConfirmationModalProps) {
  // Always call hooks — do NOT return early based on jobType.
  // Treat `jobType` as nullable and render a safe disabled state when missing.
  const [loading, setLoading] = React.useState(false);
  const [checking, setChecking] = React.useState(true);
  const [sufficient, setSufficient] = React.useState(false);
  const [currentBalance, setCurrentBalance] = React.useState(0);
  const [bonusBalance, setBonusBalance] = React.useState<number | null>(null);
  const [regularBalance, setRegularBalance] = React.useState<number | null>(
    null,
  );
  const [willUseBase, setWillUseBase] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const creditsRequired = jobType ? getCreditCost(jobType) : 0;
  const jobDescription = jobType
    ? getJobTypeDescription(jobType)
    : "Requested Action";
  const isDeferredBilling = jobType === "GUEST_INVITE";

  // Check credit sufficiency when modal opens
  React.useEffect(() => {
    if (open && userId && jobType) {
      setChecking(true);
      setError(null);

      // Guest invites are billed on acceptance (server-side), not at send time.
      // So we should not block sending based on current credit balance here.
      if (isDeferredBilling) {
        setSufficient(true);
        setCurrentBalance(0);
        setChecking(false);
        return;
      }

      checkSufficientCredits(userId, jobType)
        .then((result: any) => {
          setSufficient(result.sufficient);
          setCurrentBalance(result.currentBalance);
          // set breakdown if present
          if (typeof result.regular !== "undefined")
            setRegularBalance(result.regular);
          if (typeof result.bonus !== "undefined")
            setBonusBalance(result.bonus);
          if (typeof result.willUseBase !== "undefined")
            setWillUseBase(!!result.willUseBase);
          setChecking(false);
        })
        .catch((err) => {
          console.error("Error checking credits:", err);
          setError("Failed to check credit balance. Please try again.");
          setChecking(false);
        });
    } else if (open) {
      // If modal opened but we don't have jobType or userId, stop checking and reset state
      setChecking(false);
      setSufficient(false);
      setCurrentBalance(0);
      setError(null);
    }
  }, [open, userId, jobType]);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      const msg =
        (err as any)?.message ||
        "The output could not be generated. No credits were deducted.";
      setError(msg);
      console.error("Job execution failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] z-[10002]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-[#40e0d0]" />
            {title || `Confirm ${jobDescription}`}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {description ||
              `You are about to generate a ${jobDescription.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {checking ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">
                Checking credit balance...
              </span>
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 rounded-lg bg-destructive/10 p-4 text-destructive">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Credit cost display - required per /docs/credits.md section 6.1 */}
              {!isDeferredBilling ? (
                <div className="rounded-lg bg-muted p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Current Balance:
                    </span>
                    <span className="font-medium">
                      {formatCredits(currentBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      This Action:
                    </span>
                    <span className="font-medium text-[#40e0d0]">
                      -{formatCredits(creditsRequired)}
                    </span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Balance After:
                    </span>
                    <span className="font-medium">
                      {formatCredits(currentBalance - creditsRequired)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                  This invite will be created now.{" "}
                  <span className="font-medium text-foreground">
                    {formatCredits(creditsRequired)}
                  </span>{" "}
                  credit is consumed only if the guest accepts.
                </div>
              )}

              {/* Confirmation message - required per /docs/credits.md section 6.2 */}
              {!isDeferredBilling ? (
                <p className="text-sm text-muted-foreground">
                  This action will generate a new output and use{" "}
                  <span className="font-medium text-foreground">
                    {formatCredits(creditsRequired)}
                  </span>
                  . Credits are deducted only after successful completion.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Credits are{" "}
                  <span className="font-medium text-foreground">not</span>{" "}
                  deducted now. The host is billed only if the guest accepts.
                </p>
              )}

              {/* Insufficient credits warning */}
              {!sufficient && (
                <div className="flex items-start gap-3 rounded-lg bg-amber-500/10 p-4 text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Insufficient Credits</p>
                    <p className="text-sm">
                      You need {formatCredits(creditsRequired - currentBalance)}{" "}
                      more to complete this action.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        onOpenChange(false);
                        // Navigate to purchase page - implement as needed
                        window.location.href = "/purchase-credits";
                      }}
                    >
                      Purchase Credits
                    </Button>
                  </div>
                </div>
              )}
              {/* Warning when bonus will be used up and base credits will be charged */}
              {willUseBase && (
                <div className="flex items-start gap-3 rounded-lg bg-amber-100 p-4 text-amber-800">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">
                      Bonus credits will be used first
                    </p>
                    <p className="text-sm">
                      You have {bonusBalance ?? 0} bonus credits — the remaining{" "}
                      {formatCredits(
                        Math.max(0, creditsRequired - (bonusBalance ?? 0)),
                      )}{" "}
                      will be taken from your base credits.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || checking || !sufficient || !!error}
            className="bg-[#40e0d0] text-black hover:bg-[#40e0d0]/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              `Confirm (${formatCredits(creditsRequired)})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to use credit confirmation modal
 * Provides a simple API for triggering credit-consuming actions
 */
export function useCreditConfirmation() {
  const [modalState, setModalState] = React.useState<{
    open: boolean;
    jobType: JobType | null;
    onConfirm: (() => Promise<void>) | null;
    title?: string;
    description?: string;
  }>({
    open: false,
    jobType: null,
    onConfirm: null,
  });

  const requestConfirmation = React.useCallback(
    (
      jobType: JobType,
      onConfirm: () => Promise<void>,
      options?: { title?: string; description?: string },
    ) => {
      setModalState({
        open: true,
        jobType,
        onConfirm,
        title: options?.title,
        description: options?.description,
      });
    },
    [],
  );

  const closeModal = React.useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    modalState,
    requestConfirmation,
    closeModal,
    setModalOpen: (open: boolean) =>
      setModalState((prev) => ({ ...prev, open })),
  };
}

export default CreditConfirmationModal;
